import {JsonUtil} from "../lib/JsonUtil"


/**
 * Role-based access control configurations. The following naming schema is applied for access rights:
 *
 * Naming schema:
 *      can{Action}[Scope]{Resource}
 *
 * where:
 *      can         = Fixed prefix.
 *      {Action}    = The type of permission: [View | Create | Edit | Delete | Manage]
 *                    where "Manage" includes Create, Edit, and Delete.
 *      [Scope]     = Optional scope refinement: [All | Own].
 *      {Resource}  = The target resource: [Menu | Project | Task | Crew | Team].
 *
 * In addition to static access rights, there are also instance-based access rights, which are provided as functions
 * at the end of this module. Their naming convention follows the same schema.
 */
const accessCfg = {
    unauthenticated: {
        canViewMenuProject: false,
        canViewMenuTask:    false,
        canViewMenuSearch:  false,
        canViewMenuWorker:  false,
        canViewMenuAdmin:   false,
        canViewMenuOper:    false,

        canCreateProject:   false,
        canEditAllProject:  false,
        canEditOwnProject:  false,
        canDeleteProject:   false,
        canViewAvorProjectList: false,
        canEditAvorProject:     false,

        canCtrlTaskList:    false,
        canManageAllTask:   false,
        canManageOwnTask:   false,

        canManageAllDoc:    false,      // all rights (download, upload, delete) everywhere
        canManageOwnDoc:    false,      // all rights (download, upload, delete) for assigned projects/tasks
        canCollabOwnDoc:    false,      // collaboration rights (download, upload) for assigned tasks

        canEditCrew:        false,
        canEditCrewRoles:   [],
        canEditTeam:        false,
    },
    contractor: {
        canViewMenuProject: false,
        canViewMenuTask:    false,
        canViewMenuSearch:  false,
        canViewMenuWorker:  false,
        canViewMenuAdmin:   false,
        canViewMenuOper:    false,

        canCreateProject:   false,
        canEditAllProject:  false,
        canEditOwnProject:  false,
        canDeleteProject:   false,
        canViewAvorProjectList: false,
        canEditAvorProject:     false,

        canCtrlTaskList:    false,
        canManageAllTask:   false,
        canManageOwnTask:   false,

        canManageAllDoc:    false,
        canManageOwnDoc:    false,
        canCollabOwnDoc:    false,

        canEditCrew:        false,
        canEditCrewRoles:   [],
        canEditTeam:        false,
    },
    worker: {
        canViewMenuProject: false,
        canViewMenuTask:    true,
        canViewMenuSearch:  false,
        canViewMenuWorker:  false,
        canViewMenuAdmin:   false,
        canViewMenuOper:    false,

        canCreateProject:   false,
        canEditAllProject:  false,
        canEditOwnProject:  false,
        canDeleteProject:   false,
        canViewAvorProjectList: false,
        canEditAvorProject:     false,

        canCtrlTaskList:    false,
        canManageAllTask:   false,
        canManageOwnTask:   false,

        canManageAllDoc:    false,
        canManageOwnDoc:    false,
        canCollabOwnDoc:    true,

        canEditCrew:        false,
        canEditCrewRoles:   [],
        canEditTeam:        false,
    },
    foreman: {
        canViewMenuProject: true,
        canViewMenuTask:    true,
        canViewMenuSearch:  true,
        canViewMenuWorker:  true,
        canViewMenuAdmin:   false,
        canViewMenuOper:    false,

        canCreateProject:   true,
        canEditAllProject:  true,
        canEditOwnProject:  true,
        canDeleteProject:   true,
        canViewAvorProjectList: false,
        canEditAvorProject:     false,

        canCtrlTaskList:    true,
        canManageAllTask:   false,
        canManageOwnTask:   true,

        canManageAllDoc:    false,
        canManageOwnDoc:    true,
        canCollabOwnDoc:    true,

        canEditCrew:        false,
        canEditCrewRoles:   [],
        canEditTeam:        false,
    },
    office: {
        canViewMenuProject: true,
        canViewMenuTask:    true,
        canViewMenuSearch:  true,
        canViewMenuWorker:  true,
        canViewMenuAdmin:   false,
        canViewMenuOper:    false,

        canCreateProject:   true,
        canEditAllProject:  true,
        canEditOwnProject:  true,
        canDeleteProject:   true,
        canViewAvorProjectList: true,
        canEditAvorProject:     true,

        canCtrlTaskList:    true,
        canManageAllTask:   false,
        canManageOwnTask:   false,

        canManageAllDoc:    false,
        canManageOwnDoc:    false,
        canCollabOwnDoc:    false,

        canEditCrew:        true,
        canEditCrewRoles:   ["contractor", "worker", "foreman", "office"],
        canEditTeam:        true,
    },
    admin: {
        canViewMenuProject: true,
        canViewMenuTask:    true,
        canViewMenuSearch:  true,
        canViewMenuWorker:  true,
        canViewMenuAdmin:   true,
        canViewMenuOper:    false,

        canCreateProject:   true,
        canEditAllProject:  true,
        canEditOwnProject:  true,
        canDeleteProject:   true,
        canViewAvorProjectList: true,
        canEditAvorProject:     true,

        canCtrlTaskList:    true,
        canManageAllTask:   true,
        canManageOwnTask:   true,

        canManageAllDoc:    true,
        canManageOwnDoc:    true,
        canCollabOwnDoc:    true,

        canEditCrew:        true,
        canEditCrewRoles:   ["contractor", "worker", "foreman", "office", "admin"],
        canEditTeam:        true,
    },
    operator: {
        canViewMenuProject: true,
        canViewMenuTask:    true,
        canViewMenuSearch:  true,
        canViewMenuWorker:  true,
        canViewMenuAdmin:   true,
        canViewMenuOper:    true,

        canCreateProject:   true,
        canEditAllProject:  true,
        canEditOwnProject:  true,
        canDeleteProject:   true,
        canViewAvorProjectList: true,
        canEditAvorProject:     true,

        canCtrlTaskList:    true,
        canManageAllTask:   true,
        canManageOwnTask:   true,

        canManageAllDoc:    true,
        canManageOwnDoc:    true,
        canCollabOwnDoc:    true,

        canEditCrew:        true,
        canEditCrewRoles:   ["contractor", "worker", "foreman", "office", "admin", "operator"],
        canEditTeam:        true,
    }
};

// --------------------------------------------------------------------------------------------------------------------
// access control logic
// --------------------------------------------------------------------------------------------------------------------

const DEFAULT_USER_ID = null;
const DEFAULT_ROLE = 'unauthenticated';

let currentUserId = DEFAULT_USER_ID;
let currentRole = DEFAULT_ROLE;

export let accessCtrl = accessCfg[currentRole];

export const setAccessCtrl = (userId, userRole) => {
    if (accessCfg[userRole]) {
        currentUserId = userId;
        currentRole = userRole;
        accessCtrl = accessCfg[currentRole];
    } else {
        console.warn(`Invalid role: ${userRole}, reset access control to defaults.`);
        resetAccessCtrl();
    }
}

export const resetAccessCtrl = () => {
    currentUserId = DEFAULT_USER_ID;
    currentRole = DEFAULT_ROLE;
    accessCtrl = accessCfg[currentRole];
}

// --------------------------------------------------------------------------------------------------------------------
// instance based access control
// --------------------------------------------------------------------------------------------------------------------

export const canEditProject = (foremanCrewId, deputyForemanCrewId) => {
    if ( !currentUserId ) {
        return false;
    }
    if ( accessCtrl.canEditAllProject ) {
        return true;
    }

    if ( !accessCtrl.canEditOwnProject ) {
        return false;
    }
    return currentUserId === foremanCrewId || currentUserId === deputyForemanCrewId;
}

export const canRestoreProject = (foremanCrewId) => {
    if (!currentUserId) {
        return false;
    }

    if (accessCtrl.canEditAllProject) {
        return true;
    }

    if (!accessCtrl.canEditOwnProject) {
        return false;
    }

    return currentUserId === foremanCrewId;
};

export const canEditProjectInList = (foremanFilter, view) => {
    if ( !currentUserId ) {
        return false;
    }
    if ( accessCtrl.canEditAllProject ) {
        return true;
    }

    if ( view === 'assignView' ) {                  // ProjectList assignView
        return false;
    }

    if ( !accessCtrl.canEditOwnProject ) {
        return false;
    }
    return currentUserId === foremanFilter;
}


export const canManageTask = (foremanCrewId, deputyForemanCrewId) => {
    if ( !currentUserId ) {
        return false;
    }
    if ( accessCtrl.canManageAllTask ) {
        return true;
    }

    if ( !accessCtrl.canManageOwnTask ) {
        return false;
    }
    return currentUserId === foremanCrewId || currentUserId === deputyForemanCrewId;
}

export const canManageTaskInList = (foremanFilter) => {
    if ( !currentUserId ) {
        return false;
    }
    if ( accessCtrl.canManageAllTask ) {
        return true;
    }

    if ( !accessCtrl.canManageOwnTask ) {
        return false;
    }
    return currentUserId === foremanFilter;
}

/**
 * Check if the current user is one of the team leader or team member assigned to a task
 */
const isAssignedToTask = (teamLeadCrewId, teamMemberCrewIds= []) => {
    if ( !currentUserId ) {
        return false;
    }

    // check team lead
    if ( currentUserId === teamLeadCrewId ) {
        return true;
    }

    // check team members (null aware)
    const members = Array.isArray(teamMemberCrewIds)
        ? teamMemberCrewIds
        : JsonUtil.convertJsonStringToArray(teamMemberCrewIds);

    return members.includes(currentUserId);
}

export const canManageDoc = (foremanCrewId, deputyForemanCrewId) => {

    if ( !currentUserId ) {
        return false;
    }

    if ( accessCtrl.canManageAllDoc ) {
        return true;
    }

    if ( !accessCtrl.canManageOwnDoc ) {
        return false;
    }

    return currentUserId === foremanCrewId || currentUserId === deputyForemanCrewId;
}

export const canCollabDoc = (foremanCrewId, deputyForemanCrewId, teamLeadCrewId, teamMemberCrewIds= []) => {
    if ( !currentUserId ) {
        return false;
    }
    if ( accessCtrl.canManageAllDoc ) {
        return true;
    }

    if ( !accessCtrl.canCollabOwnDoc ) {
        return false;
    }

    return currentUserId === foremanCrewId || currentUserId === deputyForemanCrewId ||
        isAssignedToTask(teamLeadCrewId, teamMemberCrewIds);
}


// --------------------------------------------------------------------------------------------------------------------
// The end.